<template>
  <div class="main-table-wrapper">
    <el-table
      :data="data"
      :loading="loading"
      style="width: 100%">
      <el-table-column
        v-if="showIndexColumn"
        :label="indexColumnLabel != null ? indexColumnLabel : $tc('general.n0')"
        min-width="55px">
        <template slot-scope="scope">
          {{ scope.$index + 1 }}.
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hiddenFields.includes('logo')"
        :label="$tc('settings.logo')"
        width="100px">
        <template slot-scope="scope">
          <main-image :src="$file('avatars', scope.row.logo)" style="width: 75px; height: 75px" class="image" />
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hiddenFields.includes('name')"
        :label="$tc('club.club_name')">
        <template slot-scope="scope">
          <router-link :to="link ? link(scope.row) : { name: 'ClubView', params: { id: scope.row.id} }" class="link">
            {{ scope.row.name }}
          </router-link>
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hiddenFields.includes('shortName')"
        :label="$tc('club.short_name')">
        <template slot-scope="scope">
          <router-link :to="link ? link(scope.row) : { name: 'ClubView', params: { id: scope.row.id} }" class="link">
            {{ scope.row.shortName }}
          </router-link>
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hiddenFields.includes('city')"
        :label="$tc('form.city')">
        <template slot-scope="scope">
          {{ scope.row.city }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hiddenFields.includes('address')"
        :label="$tc('form.address')">
        <template slot-scope="scope">
          {{ scope.row.address }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hiddenFields.includes('email')"
        :label="$tc('form.email')">
        <template slot-scope="scope">
          {{ scope.row.email }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hiddenFields.includes('website')"
        :label="$tc('form.website')">
        <template slot-scope="scope">
          {{ scope.row.website }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hiddenFields.includes('contact')"
        :label="$tc('form.contact_person')">
        <template slot-scope="scope">
          {{ scope.row.contact }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="!hiddenFields.includes('mobile')"
        :label="$tc('form.mobile')">
        <template slot-scope="scope">
          {{ scope.row.mobile }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showPoints && !showMedalStats"
        :label="$tc('general.points')"
        width="90px"
        align="right">
        <template slot-scope="scope">
          <el-tooltip
            :content="$tc('general.points')"
            placement="top">
            <el-tag
              size="small"
              class="club-points-tag">
              {{ scope.row.points != null ? scope.row.points : (scope.row.earnedPoints != null ? scope.row.earnedPoints : 0) }}
            </el-tag>
          </el-tooltip>
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        label="Ukupno medalja"
        width="120px"
        align="center">
        <template slot-scope="scope">
          {{ getTotalMedals(scope.row) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="72px"
        align="center">
        <template #header>
          <span class="medal-count-header" title="Zlatne medalje">
            <span>Broj</span>
            <span class="medal-header-icon">🥇</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['goldMedals', 'medalsGold', 'gold_medals']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="78px"
        align="center">
        <template #header>
          <span class="medal-points-header">
            <span>Poeni</span>
            <span class="medal-header-icon" title="Poeni za zlatne medalje">🥇</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['goldPoints', 'goldMedalPoints', 'pointsGold', 'gold_points']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="72px"
        align="center">
        <template #header>
          <span class="medal-count-header" title="Srebrne medalje">
            <span>Broj</span>
            <span class="medal-header-icon">🥈</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['silverMedals', 'medalsSilver', 'silver_medals']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="78px"
        align="center">
        <template #header>
          <span class="medal-points-header">
            <span>Poeni</span>
            <span class="medal-header-icon" title="Poeni za srebrne medalje">🥈</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['silverPoints', 'silverMedalPoints', 'pointsSilver', 'silver_points']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="72px"
        align="center">
        <template #header>
          <span class="medal-count-header" title="Bronzane medalje">
            <span>Broj</span>
            <span class="medal-header-icon">🥉</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['bronzeMedals', 'medalsBronze', 'bronze_medals']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="78px"
        align="center">
        <template #header>
          <span class="medal-points-header">
            <span>Poeni</span>
            <span class="medal-header-icon" title="Poeni za bronzane medalje">🥉</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['bronzePoints', 'bronzeMedalPoints', 'pointsBronze', 'bronze_points']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        label="Ukupno poena"
        width="120px"
        align="right">
        <template slot-scope="scope">
          <el-tooltip
            :content="$tc('general.points')"
            placement="top">
            <el-tag
              size="small"
              class="club-points-tag">
              {{ scope.row.points != null ? scope.row.points : (scope.row.earnedPoints != null ? scope.row.earnedPoints : 0) }}
            </el-tag>
          </el-tooltip>
        </template>
      </el-table-column>

      <el-table-column
        v-if="showRankColumn && rankColumnAfterPoints"
        :label="rankColumnLabel != null ? rankColumnLabel : $tc('general.rank')"
        min-width="55px">
        <template slot-scope="scope">
          {{ scope.$index + 1 }}.
        </template>
      </el-table-column>

      <el-table-column
        v-else-if="showRankColumn"
        :label="rankColumnLabel != null ? rankColumnLabel : $tc('general.rank')"
        min-width="55px">
        <template slot-scope="scope">
          {{ scope.$index + 1 }}.
        </template>
      </el-table-column>

      <el-table-column
        v-if="showActions && user.isAdmin"
        :label="$tc('general.actions')"
        align="right">
        <template slot-scope="scope">
          <el-tooltip
            v-if="showRestore"
            :content="$tc('club.undelete')"
            placement="top">
            <el-button
              type="primary"
              icon="el-icon-refresh-left"
              circle
              @click="beforeRestore(scope.row)" />
          </el-tooltip>

          <el-button v-else-if="scope.row.id !== clubId" type="danger" icon="el-icon-delete" circle @click="beforeRemove(scope.row)" />
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import errorHandler from '@/common/errorHandler.js'

export default {
  props: {
    data: {
      type: Array,
      required: true
    },

    loading: {
      type: Boolean,
      default: false
    },
    fullOptions: {
      type: Boolean,
      default: false
    },
    link: {
      type: Function,
      default: null
    },

    hiddenFields: {
      type: Array,
      default: () => ['name']
    },

    showPoints: {
      type: Boolean,
      default: false
    },
    showMedalStats: {
      type: Boolean,
      default: false
    },

    showActions: {
      type: Boolean,
      default: true
    },
    // Deleted clubs get an "undelete" button instead of the delete one.
    showRestore: {
      type: Boolean,
      default: false
    },
    showIndexColumn: {
      type: Boolean,
      default: false
    },
    indexColumnLabel: {
      type: String,
      default: null
    },

    showRankColumn: {
      type: Boolean,
      default: false
    },
    rankColumnLabel: {
      type: String,
      default: null
    },
    rankColumnAfterPoints: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    clubId () {
      return this.user?.club?.id
    },

    user () {
      return this.$store.state.auth.user
    }
  },

  methods: {
    getNumericValue (row, keys) {
      for (const key of keys) {
        if (row && row[key] != null && row[key] !== '') {
          const value = Number(row[key])
          if (Number.isFinite(value)) return value
        }
      }
      return 0
    },
    getTotalMedals (row) {
      if (row && row.totalMedals != null && row.totalMedals !== '') {
        const total = Number(row.totalMedals)
        if (Number.isFinite(total)) return total
      }
      return this.getNumericValue(row, ['goldMedals', 'medalsGold', 'gold_medals']) +
        this.getNumericValue(row, ['silverMedals', 'medalsSilver', 'silver_medals']) +
        this.getNumericValue(row, ['bronzeMedals', 'medalsBronze', 'bronze_medals'])
    },
    async beforeRemove (club) {
      try {
        await this.$confirm(`${this.$tc('club.ask_delete_club')}: ${club.shortName}`)
        this.onDelete(club)
      } catch {}
    },

    async beforeRestore (club) {
      try {
        await this.$confirm(`${this.$tc('club.ask_undelete_club')}: ${club.shortName}`)
        this.onRestore(club)
      } catch {}
    },

    async onRestore (club) {
      const loading = this.$loading()
      try {
        await this.$store.dispatch('clubs/restore', club.id)
        this.$message.success(this.$tc('messages.success_restored_club'))
        this.$emit('restore', club)
      } catch (e) {
        console.error(e)
        errorHandler(e)
      } finally {
        loading.close()
      }
    },

    async onDelete (club) {
      const loading = this.$loading()
      try {
        await this.$store.dispatch('clubs/delete', club.id)
        this.$message.success(this.$tc('messages.success_deleted_club'))
        this.$emit('delete', club)
      } catch (e) {
        console.error(e)
        errorHandler(e)
      } finally {
        loading.close()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.club-points-tag {
  margin-left: 8px;
  vertical-align: middle;
  background-color: #000;
  border-color: #000;
  color: #fff;
}
.medal-header-icon {
  font-size: 18px;
  line-height: 1;
}
.medal-points-header,
.medal-count-header {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.1;
}
</style>
